import { useState, type FormEvent } from "react";

/**
 * Records what happened (ADR-089): whether a what-if's shock or a scenario came about, and when.
 * It is recorded once and never changed, so the form says so before it is sent.
 */
export function RecordOutcome({
  subject,
  onRecord,
}: {
  /** "shock" for a what-if, "scenario" for a scenario. */
  readonly subject: "shock" | "scenario";
  readonly onRecord: (input: {
    readonly happened: boolean;
    readonly on?: string;
    readonly notes: string;
  }) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [happened, setHappened] = useState<boolean>();
  const [on, setOn] = useState("");
  const [notes, setNotes] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState(false);
  const invalid =
    happened === undefined
      ? "Say whether it happened."
      : happened && on === ""
        ? "Give the date it happened."
        : notes.trim() === ""
          ? "Say how you know."
          : undefined;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (invalid !== undefined || happened === undefined) return;
    setPending(true);
    setError(undefined);
    try {
      await onRecord({ happened, ...(happened ? { on } : {}), notes: notes.trim() });
      setDone(true);
    } catch (cause) {
      setError(
        `The server did not confirm, so nothing was recorded. ${cause instanceof Error ? cause.message : ""}`.trim(),
      );
    } finally {
      setPending(false);
    }
  };
  if (done) {
    return (
      <p role="status">
        ✓ Recorded.{" "}
        {subject === "shock"
          ? "Its judgments can now be scored."
          : "The engine will not revise it again."}
      </p>
    );
  }
  if (!open) {
    return (
      <button type="button" className="button secondary" onClick={() => setOpen(true)}>
        Record what happened
      </button>
    );
  }
  const noun = subject === "shock" ? "the shock" : "this scenario";
  return (
    <form className="record-outcome" onSubmit={(event) => void submit(event)}>
      <fieldset>
        <legend>Did {noun} happen?</legend>
        <label>
          <input
            type="radio"
            name="happened"
            checked={happened === true}
            onChange={() => setHappened(true)}
          />
          Yes
        </label>
        <label>
          <input
            type="radio"
            name="happened"
            checked={happened === false}
            onChange={() => setHappened(false)}
          />
          No
        </label>
      </fieldset>
      {happened === true ? (
        <label>
          On
          <input type="date" value={on} onChange={(event) => setOn(event.target.value)} />
        </label>
      ) : null}
      <label>
        How you know
        <textarea
          value={notes}
          maxLength={2000}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>
      <p className="mono-note">
        Recorded once and never changed.{" "}
        {subject === "shock"
          ? "If it happened, the what-if's judgments are scored from that date."
          : "Once resolved, the engine makes no further revision to it."}
      </p>
      {invalid === undefined ? null : <p className="field-error">{invalid}</p>}
      {error === undefined ? null : (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <button
        type="submit"
        className="button button-primary"
        disabled={pending || invalid !== undefined}
      >
        {pending ? "Waiting for the server…" : "Record it"}
      </button>
      <button
        type="button"
        className="button secondary"
        disabled={pending}
        onClick={() => setOpen(false)}
      >
        Cancel
      </button>
    </form>
  );
}
